import { DashboardMetricCard, DashboardMetricGrid } from '@yeshuman/sdk'
import { Stethoscope, TrendingUp, AlertCircle } from 'lucide-react'

import { Symptom } from '../hooks/use-symptoms'

interface SymptomStatsProps {
  symptoms: Symptom[]
}

export function SymptomStats({ symptoms }: SymptomStatsProps) {
  const today = new Date()
  const startOfWeek = new Date(today)
  startOfWeek.setDate(today.getDate() - today.getDay())
  startOfWeek.setHours(0, 0, 0, 0)

  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

  const symptomsThisWeek = symptoms.filter((s) => {
    const symptomDate = new Date(s.occurred_at)
    return symptomDate >= startOfWeek
  })

  const symptomsThisMonth = symptoms.filter((s) => {
    const symptomDate = new Date(s.occurred_at)
    return symptomDate >= startOfMonth
  })

  const severityCounts = {
    mild: symptoms.filter((s) => s.severity === 'mild').length,
    moderate: symptoms.filter((s) => s.severity === 'moderate').length,
    severe: symptoms.filter((s) => s.severity === 'severe').length,
    very_severe: symptoms.filter((s) => s.severity === 'very_severe').length,
  }

  const symptomTypeCounts: Record<string, number> = {}
  symptoms.forEach((s) => {
    const type = s.symptom_type
    symptomTypeCounts[type] = (symptomTypeCounts[type] || 0) + 1
  })
  const mostCommonSymptom = Object.entries(symptomTypeCounts).sort((a, b) => b[1] - a[1])[0]?.[0]

  const anatomyCounts: Record<string, number> = {}
  symptoms.forEach((s) => {
    const location = s.anatomy.name
    anatomyCounts[location] = (anatomyCounts[location] || 0) + 1
  })
  const mostCommonAnatomy = Object.entries(anatomyCounts).sort((a, b) => b[1] - a[1])[0]?.[0]

  return (
    <DashboardMetricGrid>
      <DashboardMetricCard
        title="Total symptoms"
        value={symptoms.length}
        description="All time"
        icon={Stethoscope}
      />
      <DashboardMetricCard
        title="This week"
        value={symptomsThisWeek.length}
        description={`Since ${startOfWeek.toLocaleDateString()}`}
        icon={TrendingUp}
      />
      <DashboardMetricCard
        title="This month"
        value={symptomsThisMonth.length}
        description={`Since ${startOfMonth.toLocaleDateString()}`}
        icon={TrendingUp}
      />
      {symptoms.length > 0 ? (
        <DashboardMetricCard
          title="Severity distribution"
          value={
            <div className="space-y-1 text-base font-normal">
              <div className="flex justify-between text-sm">
                <span>Mild:</span>
                <span className="font-medium">{severityCounts.mild}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Moderate:</span>
                <span className="font-medium">{severityCounts.moderate}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Severe:</span>
                <span className="font-medium">{severityCounts.severe}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Very severe:</span>
                <span className="font-medium">{severityCounts.very_severe}</span>
              </div>
            </div>
          }
          icon={AlertCircle}
        />
      ) : null}
      {mostCommonSymptom ? (
        <DashboardMetricCard
          title="Most common"
          value={<span className="capitalize">{mostCommonSymptom}</span>}
          description={`${symptomTypeCounts[mostCommonSymptom]} times`}
          icon={Stethoscope}
        />
      ) : null}
      {mostCommonAnatomy ? (
        <DashboardMetricCard
          title="Most common location"
          value={<span className="capitalize">{mostCommonAnatomy}</span>}
          description={`${anatomyCounts[mostCommonAnatomy]} times`}
          icon={Stethoscope}
        />
      ) : null}
    </DashboardMetricGrid>
  )
}
